import React from "react";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import {
  getTerminalServiceErrorMessage,
  isTerminalServiceUploadedEntry,
  useTerminalServiceDocumentUpload,
} from "@features/terminal-services/hooks/useTerminalServiceDocumentUpload";
import type {
  GantryCraneAdditionalServiceDocuments,
  TerminalServiceDocument,
} from "@services/terminal-services/types";
import {
  createGantryCraneServiceReqest,
  resubmitGantryCraneServiceRequest,
} from "@services/terminalServicesApi";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FOTT_SERVICE_REQUEST_FIELDS as F, OPERATOR_LICENSE_DOCUMENT_TYPE } from "../constants";
import { mapGantryCraneFormToPayload } from "../mappers";
import type { FottServiceRequestFormData } from "../schema";

const isFileAttachment = (value: unknown): value is File =>
  typeof File !== "undefined" && value instanceof File;

const getUploadedAttachmentDocuments = (
  formData: FottServiceRequestFormData,
): GantryCraneAdditionalServiceDocuments[] | null => {
  return (
    (formData[F.ATTACHMENT]?.filter(
      (attachment) => !isFileAttachment(attachment) && !!attachment.documentUrl,
    ) as GantryCraneAdditionalServiceDocuments[] | undefined) ?? null
  );
};
const getUploadedOperatorLicenseDocument = (
  entries: UploadEntry[],
): TerminalServiceDocument | null => {
  const uploadedEntry = entries.find(isTerminalServiceUploadedEntry);

  if (!uploadedEntry) {
    return null;
  }

  return {
    documentUrl: uploadedEntry.url,
    documentName: uploadedEntry.name,
    documentType: OPERATOR_LICENSE_DOCUMENT_TYPE,
  };
};

export const useCreateGantryCraneServiceRequest = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const {
    uploadEntries: operatorLicenseFiles,
    setUploadEntries: setOperatorLicenseFiles,
    handleUpload: handleOperatorLicenseUpload,
    resetUpload: resetOperatorLicenseUpload,
    isUploading: isUploadingOperatorLicense,
  } = useTerminalServiceDocumentUpload({
    storeKey: F.OPERATOR_LICENSE,
    certificateType: OPERATOR_LICENSE_DOCUMENT_TYPE,
    fallbackFileNamePrefix: "operator-license",
    errorNotification: {
      titleKey: "terminal-services.fott-service-request.notifications.upload-error-title",
      descriptionKey:
        "terminal-services.fott-service-request.notifications.upload-error-description",
    },
  });

  const createRequestMutation = useMutation({
    mutationFn: createGantryCraneServiceReqest,
    onSuccess: () => {
      notify({
        title: t("terminal-services.fott-service-request.notifications.submit-success-title"),
        type: "success",
      });
    },
    onError: (error) => {
      notify({
        title: t("terminal-services.fott-service-request.notifications.submit-error-title"),
        description:
          getTerminalServiceErrorMessage(error) ??
          t("terminal-services.fott-service-request.notifications.submit-error-description"),
        type: "error",
      });
      console.error("Create Gantry Crane service request failed:", error);
    },
  });
  const resubmitRequestMutation = useMutation({
    mutationFn: resubmitGantryCraneServiceRequest,
    onSuccess: () => {
      notify({
        title: t("terminal-services.fott-service-request.notifications.submit-success-title"),
        type: "success",
      });
    },
    onError: (error) => {
      notify({
        title: t("terminal-services.fott-service-request.notifications.submit-error-title"),
        description:
          getTerminalServiceErrorMessage(error) ??
          t("terminal-services.fott-service-request.notifications.submit-error-description"),
        type: "error",
      });

      console.error("Resubmit gantry crane service request failed:", error);
    },
  });

  const resubmitRequest = React.useCallback(
    async (id: string, formValues: FottServiceRequestFormData) => {
      try {
        const payload = mapGantryCraneFormToPayload({
          values: formValues,
          operatorLicenseDocument: getUploadedOperatorLicenseDocument(operatorLicenseFiles),
          attachmentDocuments: getUploadedAttachmentDocuments(formValues),
        });

        await resubmitRequestMutation.mutateAsync({
          id,
          payload,
        });

        return true;
      } catch {
        return false;
      }
    },
    [operatorLicenseFiles, resubmitRequestMutation],
  );

  const submitRequest = React.useCallback(
    async (formValues: FottServiceRequestFormData) => {
      try {
        const operatorLicenseDocument = getUploadedOperatorLicenseDocument(operatorLicenseFiles);
        const payload = mapGantryCraneFormToPayload({
          values: formValues,
          operatorLicenseDocument,
          attachmentDocuments: getUploadedAttachmentDocuments(formValues),
        });

        await createRequestMutation.mutateAsync(payload);
        return true;
      } catch {
        return false;
      }
    },
    [createRequestMutation, operatorLicenseFiles],
  );

  return {
    operatorLicenseFiles,
    setOperatorLicenseFiles,
    handleOperatorLicenseUpload,
    removeOperatorLicense: resetOperatorLicenseUpload,
    resetOperatorLicenseUpload,
    isUploadingOperatorLicense,
    isSubmitting:
      createRequestMutation.isPending ||
      resubmitRequestMutation.isPending ||
      isUploadingOperatorLicense,
    submitRequest,
    resubmitRequest,
  };
};
